import { ComponentProps } from 'react';

import { Link } from '@mui/material';
import dayjs from 'dayjs';
import pluralize from 'pluralize';

import { DATE_TIME_PRECISE } from '@cast/constants';
import {
  Column,
  Icons,
  mergeSx,
  useThemeColor,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

import { Link as RouterLink } from 'components/router';

import { EventDetailsSubRow } from './_components/EventDetailsSubRow';
import { ExplainRecommendationCell } from './_components/ExplainRecommendationCell';
import { countConfigActions } from './_utils';
import { LogoSmallSvg } from '../../../../../assets/svg';
import { autoscalerModeDisplayName } from '../../_constants';
import { makeEventDetailsLink as defaultMakeEventDetailsLink } from '../../navigation';
import {
  isCastEvent,
  isConfigChangedEvent,
  isErrorEvent,
  isRecommendedRequestsChangedEvent,
  PreparedEvent,
} from '../../types';

import { useIsOpsPilotEnabled } from '@cast/features-organization-shared-opspilot';
import { FailedToLoad } from '@cast/shared-messages-ui';

import {
  NoData,
  NoResults,
  SearchableTable,
  useSearchContext,
  SearchableTableRecordsCount,
} from '@cast/shared-search-ui';

export type MakeEventDetailsLink = (args: {
  eventId: string;
  createdAt: string;
}) => string;

const EventLink = ({
  event,
  makeEventDetailsLink,
  sx,
  ...props
}: {
  event: PreparedEvent;
  makeEventDetailsLink: MakeEventDetailsLink;
} & ComponentProps<typeof Link>) => {
  return (
    <Link
      to={makeEventDetailsLink({
        eventId: event.id,
        createdAt: event.createdAt,
      })}
      component={RouterLink}
      replace={false}
      sx={mergeSx(
        {
          textDecoration: 'none',
          fontWeight: 500,
        },
        sx
      )}
      {...props}
    />
  );
};

const EventNameCell = ({
  event,
  makeEventDetailsLink,
}: {
  event: PreparedEvent;
  makeEventDetailsLink: MakeEventDetailsLink;
}) => {
  const { displayName } = event;
  const blue500 = useThemeColor('blue.500');

  if (isErrorEvent(event)) {
    return (
      <Stack direction="row" gap={4} alignItems="center" typography="inherit">
        <Icons.Warning
          weight="fill"
          size={16}
          css={({ palette }) => ({ color: palette.red[500] })}
        />
        <EventLink event={event} makeEventDetailsLink={makeEventDetailsLink}>
          {displayName}
        </EventLink>
      </Stack>
    );
  }

  if (isCastEvent(event)) {
    return (
      <Stack direction="row" gap={4} alignItems="center" typography="inherit">
        <LogoSmallSvg width={12} color={blue500} />
        <EventLink event={event} makeEventDetailsLink={makeEventDetailsLink}>
          {displayName}
        </EventLink>
      </Stack>
    );
  }

  if (isConfigChangedEvent(event)) {
    const configActionsCount = countConfigActions(event.details);
    return (
      <Stack>
        <div>
          <EventLink event={event} makeEventDetailsLink={makeEventDetailsLink}>
            {displayName}
          </EventLink>
        </div>
        {configActionsCount > 0 && (
          <Typography
            variant="P10R"
            color={tokens.theme.fg.structural.secondary}
          >
            {configActionsCount} {pluralize('actions', configActionsCount)}
          </Typography>
        )}
      </Stack>
    );
  }

  if (isRecommendedRequestsChangedEvent(event)) {
    const applyType = autoscalerModeDisplayName[event.details.applyType];
    return (
      <EventLink event={event} makeEventDetailsLink={makeEventDetailsLink}>
        {displayName}
        {applyType ? `: ${applyType}` : ''}
      </EventLink>
    );
  }

  return (
    <EventLink event={event} makeEventDetailsLink={makeEventDetailsLink}>
      {displayName}
    </EventLink>
  );
};

type Props = {
  hideImpactedWorkloadsColumn?: boolean;
  maxHeight?: string;
  makeEventDetailsLink?: MakeEventDetailsLink;
};

export const EventsTable = ({
  hideImpactedWorkloadsColumn,
  maxHeight,
  makeEventDetailsLink = defaultMakeEventDetailsLink,
}: Props) => {
  const searchCtx = useSearchContext();
  const { opsPilotStatus } = useIsOpsPilotEnabled();
  const isOpsPilotOn = opsPilotStatus === 'on';

  return (
    <Box flexGrow={1}>
      <SearchableTable<PreparedEvent>
        maxHeight={maxHeight}
        fillParentHeight
        components={{
          noData: (
            <NoData
              icon={<Icons.SquareHalf />}
              entity="workload autoscaler event"
            />
          ),
          noResults: (
            <NoResults
              icon={<Icons.SquareHalf />}
              entity="workload autoscaler event"
              sx={{ mb: 32 }}
            />
          ),
          failed: (
            <FailedToLoad
              sx={{ pt: 32 }}
              title="Failed to load autoscaler events"
            />
          ),
        }}
        onRowClick={(_, { tableApi, row }) => tableApi.toggleIsRowExpanded(row)}
        getExpandedPanel={EventDetailsSubRow}
        getRowClass={(event) =>
          isErrorEvent(event) ? 'error-marker' : undefined
        }
        outerHeader={<SearchableTableRecordsCount title="Event" />}
        getRowTestId={(_, { index }) => `events-row-${index}`}
        sx={{
          '.error-marker > .DS-Table-Row': {
            position: 'relative',
            '&:before': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              width: 2,
              backgroundColor: 'red.500',
            },
          },
        }}
        testId="events-table"
      >
        <Column<PreparedEvent>
          id="timestamp"
          header="Timestamp"
          width={310}
          renderCell={({ createdAt }) =>
            dayjs
              .utc(createdAt)
              .tz(searchCtx?.values.dateRange?.timezone)
              .format(DATE_TIME_PRECISE)
          }
        />
        <Column<PreparedEvent>
          id="displayName"
          header="Event"
          minWidth={187}
          renderCell={(event) => (
            <EventNameCell
              event={event}
              makeEventDetailsLink={makeEventDetailsLink}
            />
          )}
        />
        <Column<PreparedEvent>
          id="workloads"
          header={
            hideImpactedWorkloadsColumn ? undefined : 'Impacted workloads'
          }
          minWidth={220}
          renderCell={({ workloads }) =>
            hideImpactedWorkloadsColumn
              ? null
              : workloads?.length
              ? workloads?.map((w) => w.name).join(', ')
              : '-'
          }
        />
        {isOpsPilotOn && (
          <Column<PreparedEvent>
            id="castpilot-assistant"
            header=""
            width={125}
            cellWrapperSx={{ justifyContent: 'flex-end', pr: 12 }}
            renderCell={(event) => <ExplainRecommendationCell event={event} />}
          />
        )}
      </SearchableTable>
    </Box>
  );
};
